import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../../lib/api'
import { useAuthSession } from '../../lib/authSession'
import { formatNumberString, formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import SourceDocumentList from '../documents/SourceDocumentList'
import { catalogue, errorText } from '../recordEdits/api'
import { recordHref as contextRecordHref } from '../navigation/context'
import { formatBookValue, type BookFieldDefinition } from '../rkdecks/bookReview'
import { decideRemoval, reviewRemoval } from './api'
import {
  removalDocuments,
  removalFields,
  removalStatus,
  sameRemovalValue,
  targetName,
  type RemovalAction,
  type RemovalDecision,
  type RemovalRequest,
  type RemovalReview,
} from './contract'
import '../rkdecks/book-review.css'
import './removals.css'

interface Props {
  requestId: string
  embedded?: boolean
  targetLabel?: string
  recordHref?: string
  overviewHref?: string
  onChanged?: (request: RemovalRequest) => void
}

export default function RemovalRequestPane(props: Props) {
  const session = useAuthSession()
  return <RemovalReviewPane key={`${props.requestId}:${session ?? ''}`} {...props} />
}

function valueText(value: unknown, field: BookFieldDefinition): string {
  if (value === undefined) return 'Not available'
  if (value === null) return 'Not recorded'
  if (field.format === 'date') return formatOfficerDate(String(value)) ?? String(value)
  if (['number', 'quantity', 'money'].includes(field.format))
    return formatNumberString(String(value))
  const text = formatBookValue({ state: 'value', value })
  return field.value_kind === 'enum' ? humanizeToken(text) : text
}

function RemovalReviewPane({
  requestId,
  embedded,
  targetLabel,
  recordHref,
  overviewHref,
  onChanged,
}: Props) {
  const [review, setReview] = useState<RemovalReview | null>(null)
  const [fields, setFields] = useState<BookFieldDefinition[]>([])
  const [full, setFull] = useState(false)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [version, setVersion] = useState(0)
  const mounted = useRef(true)
  const inFlight = useRef(false)
  const intent = useRef<{ signature: string; body: RemovalDecision } | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(() => {
    let active = true
    const controller = new AbortController()
    setBusy(true)
    setError(null)
    setReview(null)
    setFields([])
    reviewRemoval(requestId, controller.signal)
      .then(async (result) => {
        if (
          result.request.request_id !== requestId ||
          result.original.target_id !== result.request.target_id ||
          result.original.target_type !== result.request.target_type
        )
          throw new Error('This removal review belongs to a different request.')
        if (
          result.current &&
          (result.current.target_id !== result.request.target_id ||
            result.current.target_type !== result.request.target_type)
        )
          throw new Error('The current Record belongs to a different removal request.')
        // Reject/cancel remain available if only the field catalogue fails;
        // approval waits until the current object can actually be displayed.
        if (active) setReview({ ...result, can_approve: false })
        const definitions = await catalogue(result.request.target_type)
        if (active) {
          setReview(result)
          setFields(definitions.fields)
          intent.current = null
        }
      })
      .catch((failure: unknown) => {
        if (active && !(failure instanceof DOMException && failure.name === 'AbortError'))
          setError(errorText(failure))
      })
      .finally(() => {
        if (active) setBusy(false)
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [requestId, version])

  async function decide(action: RemovalAction) {
    if (!review || busy || inFlight.current || review.request.status !== 'pending') return
    const allowed =
      action === 'approve'
        ? review.can_approve && !!review.binding
        : action === 'reject'
          ? review.can_reject
          : review.can_cancel
    if (!allowed) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    const signature = JSON.stringify([
      action,
      action === 'approve' ? review.binding : null,
      reason.trim() || null,
    ])
    if (intent.current?.signature !== signature)
      intent.current = {
        signature,
        body: {
          idempotency_key: crypto.randomUUID(),
          ...(action === 'approve' ? { binding: review.binding } : {}),
          reason: reason.trim() || null,
        },
      }
    try {
      const result = await decideRemoval(requestId, action, intent.current.body)
      if (
        result.request_id !== requestId ||
        result.target_type !== review.request.target_type ||
        result.target_id !== review.request.target_id
      )
        throw new Error('The removal response belongs to a different request.')
      if (!mounted.current) return
      setReview({
        ...review,
        request: result,
        current: null,
        binding: null,
        can_approve: false,
        can_reject: false,
        can_cancel: false,
        blockers: [],
        changed_since_request: false,
      })
      setReason('')
      onChanged?.(result)
    } catch (failure) {
      if (!mounted.current) return
      setError(errorText(failure))
      if (failure instanceof ApiError && [403, 409].includes(failure.status))
        setReview({
          ...review,
          binding: null,
          can_approve: false,
          can_reject: false,
          can_cancel: false,
        })
    } finally {
      inFlight.current = false
      if (mounted.current) setBusy(false)
    }
  }

  const request = review?.request
  const pending = request?.status === 'pending'
  const name = review
    ? targetName(review.current ?? review.original, targetLabel)
    : 'Removal request'
  const current = pending ? (review?.current ?? null) : null
  const compare = !!(review?.changed_since_request && current)
  const shown = review ? removalFields(fields, review.original, current, full) : []
  const recordDoor =
    recordHref ??
    (request
      ? contextRecordHref(
          { kind: 'fund', fund_id: request.requested_fund_id },
          { focus: `${request.target_type}:${request.target_id}` }
        )
      : null)
  const references = current?.related_references ?? review?.original.related_references ?? []

  return (
    <section
      className="book-record-comparison removal-review"
      aria-label="Removal request review"
      data-testid="removal-request-review"
    >
      {!embedded ? (
        <header className="book-record-comparison__band">
          <span>Book</span>
        </header>
      ) : null}
      {review && request ? (
        <>
          <p className="book-record-comparison__sentence">
            {request.requester.name ?? 'An officer'} requested removal of {name}.
          </p>
          <div className="removal-review__summary">
            <p role="status">
              <strong>{removalStatus(request.status)}</strong> ·{' '}
              {formatOfficerDate(request.requested_at)}
            </p>
            <p>{request.reason}</p>
            <nav aria-label="Removal context">
              {recordDoor && pending ? <Link to={recordDoor}>Record →</Link> : null}
              {overviewHref ? <Link to={overviewHref}>Subchain overview →</Link> : null}
            </nav>
            {review.changed_since_request ? (
              <p className="removal-review__notice">
                The Record or its references changed since this request. Review the current values
                below.
              </p>
            ) : null}
            {!pending ? (
              <p>{request.message}</p>
            ) : !review.current ? (
              <p>Current Record unavailable. Removal cannot be approved.</p>
            ) : null}
            {request.decision ? (
              <p>
                {request.decision.actor?.name ?? 'Decision recorded'} ·{' '}
                {formatOfficerDate(request.decision.decided_at)}
                {request.decision.reason ? ` · ${request.decision.reason}` : ''}
              </p>
            ) : null}
          </div>
          <div className="book-record-comparison__objects">
            <section className="book-record-comparison__object">
              <header className="book-record-comparison__object-header">
                <div>
                  <h3>{name}</h3>
                  <p>
                    {pending
                      ? 'Requested change: remove this object'
                      : 'Object at the time of the request'}
                  </p>
                </div>
              </header>
              <div
                className={`book-record-comparison__columns removal-review__fields ${compare ? 'with-original' : ''}`}
                role="table"
                aria-label="Record proposed for removal"
              >
                <div className="book-record-comparison__head" role="row">
                  <span role="columnheader">Field</span>
                  {compare ? <span role="columnheader">At request</span> : null}
                  <span role="columnheader">{current ? 'Current Record' : 'At request'}</span>
                </div>
                {shown.map((field) => (
                  <div
                    className={`book-record-comparison__row${compare && !sameRemovalValue(review.original.target[field.key], current?.target[field.key]) ? ' is-changed' : ''}`}
                    role="row"
                    key={field.key}
                  >
                    <span className="book-record-comparison__field-label" role="rowheader">
                      {field.label}
                    </span>
                    {compare ? (
                      <span role="cell">{valueText(review.original.target[field.key], field)}</span>
                    ) : null}
                    <span role="cell">
                      {valueText((current ?? review.original).target[field.key], field)}
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="book-record-comparison__expand"
                onClick={() => setFull(!full)}
              >
                {full ? 'Show recorded fields' : 'See full object'}
              </button>
              {references.length ? (
                <div className="removal-review__references">
                  <h4>
                    {pending ? 'Reference changes on approval' : 'Reference changes requested'}
                  </h4>
                  {references.map((reference) => (
                    <p key={`${reference.target_type}:${reference.target_id}:${reference.field}`}>
                      <strong>
                        {reference.target_label ?? `${reference.target_type} reference`}
                      </strong>{' '}
                      ·{' '}
                      {reference.field === 'ifm_entity_id'
                        ? 'Investment fund manager'
                        : 'Pledge beneficiary'}
                      : {name} → Not recorded
                    </p>
                  ))}
                </div>
              ) : null}
              {review.blockers.length ? (
                <div className="removal-review__notice" role="alert">
                  {review.blockers.map((blocker) => (
                    <p key={blocker}>{blocker}</p>
                  ))}
                </div>
              ) : null}
              {pending && (review.can_approve || review.can_reject || review.can_cancel) ? (
                <div className="removal-review__decision">
                  <label>
                    Decision note (optional)
                    <textarea
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      maxLength={2000}
                      disabled={busy}
                    />
                  </label>
                  <div className="book-record-comparison__actions">
                    {review.can_approve ? (
                      <button
                        type="button"
                        className="book-record-comparison__action book-record-comparison__action--primary"
                        disabled={busy || !review.binding || !review.current}
                        onClick={() => void decide('approve')}
                      >
                        Approve removal
                      </button>
                    ) : null}
                    {review.can_reject ? (
                      <button
                        type="button"
                        className="book-record-comparison__action"
                        disabled={busy}
                        onClick={() => void decide('reject')}
                      >
                        Reject removal
                      </button>
                    ) : null}
                    {review.can_cancel ? (
                      <button
                        type="button"
                        className="book-record-comparison__action"
                        disabled={busy}
                        onClick={() => void decide('cancel')}
                      >
                        Cancel request
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </section>
          </div>
          <SourceDocumentList documents={removalDocuments(review)} />
        </>
      ) : null}
      <div className="removal-review__summary">
        {busy ? (
          <p role="status">{review ? 'Saving the decision…' : 'Loading removal review…'}</p>
        ) : null}
        {error ? <p role="alert">{error}</p> : null}
        <button
          type="button"
          className="book-record-comparison__action"
          disabled={busy}
          onClick={() => {
            intent.current = null
            setVersion((value) => value + 1)
          }}
        >
          Refresh review
        </button>
      </div>
    </section>
  )
}
